import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { scannerApi, apiMessage, extractData } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { PERMISSIONS, hasPermission } from 'shared/util/permissions'
import { PermissionEmpty, EmptyState, Segmented, StatusPill, Skeleton } from 'shared/components/ui/primitives'
import SearchBar from 'shared/components/searchbar'
import Loader from 'shared/components/ui/loader'
import { convertDateFormat } from 'shared/util'

function attendanceStatus(user) {
  if (user?.bNotInYet) return 'out'
  if (user?.bPartiallyIn) return 'partial'
  if (user?.bIn) return 'in'
  return 'out'
}

function RegisteredUsers() {
  const { eventId } = useParams()
  const { user } = useAuth()
  const [filter, setFilter] = useState('')
  const [searchItem, setSearchItem] = useState('')
  const [debounced, setDebounced] = useState('')
  const [orders, setOrders] = useState([])
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const canResend = hasPermission(user, eventId, PERMISSIONS.RESEND_EMAIL)

  const load = useCallback(
    async (reset = false, nextSkip = 0) => {
      try {
        if (reset) setLoading(true)
        const params = { sSearch: debounced, nSkip: nextSkip, nLimit: 10 }
        if (filter) params.eFilterKey = filter
        const res = await scannerApi.registeredUsers(eventId, params)
        const data = extractData(res)
        const next = data?.aOrders || []
        setOrders((prev) => (reset ? next : [...prev, ...next]))
        setHasMore(next.length >= 10)
      } catch (err) {
        if (reset) setOrders([])
        toast.error(apiMessage(err))
      } finally {
        setLoading(false)
      }
    },
    [debounced, eventId, filter]
  )

  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchItem), 400)
    return () => clearTimeout(id)
  }, [searchItem])

  useEffect(() => {
    setSkip(0)
    load(true, 0)
  }, [load])

  const resend = async (order) => {
    try {
      const res = await scannerApi.resendEmail(eventId, order._id || order.iOrderId)
      toast.success(res?.data?.sMessage || 'Email sent')
    } catch (err) {
      toast.error(apiMessage(err))
    }
  }

  if (!hasPermission(user, eventId, PERMISSIONS.GET_REPORT)) return <PermissionEmpty />

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-5">
      <h2 className="mb-3 text-4xl font-bold">Registered users</h2>
      <Segmented
        value={filter}
        onChange={setFilter}
        options={[
          { value: '', label: 'All' },
          { value: 'IN', label: 'In' },
          { value: 'OUT', label: 'Out' }
        ]}
        className="mb-3"
      />
      <SearchBar searchItem={searchItem} onChangeFn={setSearchItem} placeHolder="Search name, email, order" className="mb-4" />

      {loading && orders.length === 0
        ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
          )
        : orders.length === 0
          ? (
        <EmptyState title="No bookings found" message="Try another search or filter." />
            )
          : (
        <div
          className="space-y-3"
          onScroll={(e) => {
            const el = e.currentTarget
            if (!hasMore || loading) return
            if (el.scrollHeight - el.scrollTop < el.clientHeight + 80) {
              const next = skip + 10
              setSkip(next)
              load(false, next)
            }
          }}
        >
          {orders.map((order) => {
            const attendee = order.oUser || {}
            const name = [attendee.sFirstName, attendee.sLastName].filter(Boolean).join(' ') || 'Guest'
            return (
              <div key={order._id || order.iOrderId} className="card-surface p-4">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <div>
                    <p className="mb-0 text-xl font-semibold">{name}</p>
                    <p className="mb-0 text-xs text-muted">{attendee.sEmail}</p>
                  </div>
                  <StatusPill status={attendanceStatus(attendee)} />
                </div>
                <p className="mb-2 text-xs text-muted">
                  {order.nTicket} ticket{order.nTicket > 1 ? 's' : ''} · {order.oPlan?.sTitle} · {order.dBookingDate ? convertDateFormat(order.dBookingDate) : ''}
                </p>
                {canResend && (
                  <button type="button" className="min-h-tap text-sm font-semibold text-theme" onClick={() => resend(order)}>
                    Resend email
                  </button>
                )}
              </div>
            )
          })}
          {loading && (
            <div className="flex justify-center py-3">
              <Loader />
            </div>
          )}
        </div>
            )}
    </div>
  )
}

export default RegisteredUsers
